import { useMemo, useState } from "react"
import { Box, Button, Card, CardContent, Divider, Stack, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { Repeat } from "lib/@components/repeat"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { formatNumber } from "../format-utils"
import { CostFacilityCostRow } from "./cost-facility-cost-row"
import { FacilityDrillDownDialog } from "./facility-drill-down-dialog"
import { AllFacilitiesDrawer } from "./all-facilities-drawer"

const INLINE_FACILITY_LIMIT = 2

export function CostFacilitiesCard({
    facilityBreakdown,
    currencySymbol,
    costResult,
    scenarioName,
    regimeName,
    onEditScenario,
    detailArgs,
}) {
    const count = facilityBreakdown?.length || 0
    const totalAssets = sumAssets()
    const sortedFacilities = useMemo(sortFacilitiesByCost, [facilityBreakdown])
    const inlineFacilities = useMemo(getInlineFacilities, [sortedFacilities])
    const hasMore = count > INLINE_FACILITY_LIMIT
    const [allFacilitiesOpen, setAllFacilitiesOpen] = useState(false)
    const openDrillDown = useDialog(<FacilityDrillDownDialog />, { maxWidth: "md", fullWidth: true })

    return (
        <Card sx={{ flex: 1.2, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <CardContent
                sx={{
                    p: 2.5,
                    "&:last-child": { pb: 4 },
                    flex: 1,
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={{ fontSize: "18px !important" }}>
                            {scenarioName || regimeName || "Scenario Details"}
                        </Typography>
                        <If value={onEditScenario}>
                            <Button
                                data-cy="edit-scenario-btn"
                                size="small"
                                variant="text"
                                color="primary"
                                startIcon={<Iconify icon="solar:pen-bold" />}
                                onClick={onEditScenario}
                                sx={{ fontWeight: 600 }}
                            >
                                Edit Scenario
                            </Button>
                        </If>
                    </Stack>

                    <Divider sx={{ borderStyle: "dashed" }} />

                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ color: "text.primary" }}>
                                <If value={regimeName}>
                                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                                        <Iconify icon="pajamas:issue-type-maintenance" sx={{ fontSize: 16 }} />
                                        <Typography variant="body2" noWrap sx={{ fontSize: 16 }}>
                                            {regimeName}
                                        </Typography>
                                    </Stack>
                                    <Box
                                        sx={{
                                            width: 3,
                                            height: 3,
                                            borderRadius: "50%",
                                            bgcolor: "text.disabled",
                                            flexShrink: 0,
                                        }}
                                    />
                                </If>
                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                                    <Iconify icon="lucide:building" sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ fontSize: 16 }}>
                                        {count} {count === 1 ? "Facility" : "Facilities"}
                                    </Typography>
                                </Stack>
                                <Box
                                    sx={{
                                        width: 3,
                                        height: 3,
                                        borderRadius: "50%",
                                        bgcolor: "text.disabled",
                                        flexShrink: 0,
                                    }}
                                />
                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                                    <Iconify icon="proicons:box" sx={{ fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ fontSize: 16 }}>
                                        {formatNumber(totalAssets)} {totalAssets === 1 ? "Asset" : "Assets"}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>

                    <Divider sx={{ borderStyle: "dashed" }} />

                    <If value={count > 0}>
                        <Stack spacing={0} divider={<Divider sx={{ borderStyle: "dashed" }} />}>
                            <Repeat
                                list={inlineFacilities}
                                item={
                                    <CostFacilityCostRow
                                        currencySymbol={currencySymbol}
                                        onFacilityClick={handleFacilityClick}
                                    />
                                }
                            />
                        </Stack>
                        <If value={hasMore}>
                            <Button
                                data-cy="view-all-facilities-btn"
                                size="small"
                                variant="text"
                                color="primary"
                                endIcon={<Iconify icon="mdi:chevron-right" />}
                                onClick={handleOpenAllFacilities}
                                sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                            >
                                View all {count} facilities
                            </Button>
                        </If>
                        <Else>
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                                No facility cost data available.
                            </Typography>
                        </Else>
                    </If>
                </Stack>
            </CardContent>
            <AllFacilitiesDrawer
                open={allFacilitiesOpen}
                onClose={handleCloseAllFacilities}
                facilities={sortedFacilities}
                currencySymbol={currencySymbol}
                onFacilityClick={handleFacilityClick}
            />
        </Card>
    )

    function sumAssets() {
        return (facilityBreakdown || []).reduce((sum, f) => sum + (f.assetCount || 0), 0)
    }

    function sortFacilitiesByCost() {
        return [...(facilityBreakdown || [])].sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0))
    }

    function getInlineFacilities() {
        return sortedFacilities.slice(0, INLINE_FACILITY_LIMIT)
    }

    function handleOpenAllFacilities() {
        setAllFacilitiesOpen(true)
    }

    function handleCloseAllFacilities() {
        setAllFacilitiesOpen(false)
    }

    async function handleFacilityClick(facility) {
        await openDrillDown({ facility, costResult, currencySymbol, detailArgs })
    }
}
